import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Heart, IndianRupee } from "lucide-react";

const impactData = [
  { amount: 500, en: "Feeds 10 people at a community kitchen", hi: "सामुदायिक रसोई में 10 लोगों का भोजन" },
  { amount: 1000, en: "Plants 20 saplings for environment protection", hi: "20 पौधे रोपण" },
  { amount: 2500, en: "Supports 1 elder for a week at an old-age home", hi: "1 बुज़ुर्ग का एक सप्ताह का समर्थन" },
  { amount: 5000, en: "Educates 1 child for a month with books & uniform", hi: "1 बच्चे की एक माह की शिक्षा" },
];

const purposes = [
  { value: "General Donation", en: "General Donation", hi: "सामान्य दान" },
  { value: "Help for the Poor", en: "Help for the Poor", hi: "गरीबों की मदद" },
  { value: "Welfare of the Elderly", en: "Welfare of the Elderly", hi: "बुजुर्गों का कल्याण" },
  { value: "Education", en: "Education", hi: "शिक्षा" },
  { value: "Medical Aid", en: "Medical Aid", hi: "चिकित्सा सहायता" },
  { value: "Women & Child Welfare", en: "Women & Child Welfare", hi: "महिला और बाल कल्याण" },
  { value: "Divyang Welfare", en: "Divyang (Disability) Welfare", hi: "दिव्यांग कल्याण" },
  { value: "Rural Development", en: "Rural Development", hi: "ग्रामीण विकास" },
  { value: "Environment Protection", en: "Environment Protection", hi: "पर्यावरण संरक्षण" },
  { value: "Disaster Relief", en: "Disaster Relief", hi: "आपदा राहत" },
];

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

const Donate = () => {
  const { t, lang } = useApp();
  const [amount, setAmount] = useState(1000);
  const [form, setForm] = useState({ donor_name: "", donor_email: "", donor_phone: "", purpose: "General Donation" });
  const [showPublicly, setShowPublicly] = useState(true);
  const [config, setConfig] = useState({ configured: false });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/payments/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const handlePay = async (e) => {
    e.preventDefault();
    if (!amount || amount < 1) {
      toast.error(lang === "hi" ? "कृपया राशि दर्ज करें" : "Please enter an amount");
      return;
    }
    setLoading(true);
    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) throw new Error("Razorpay SDK failed to load");
      const orderRes = await api.post("/payments/create-order", {
        amount,
        donor_name: form.donor_name,
        donor_email: form.donor_email,
        donor_phone: form.donor_phone,
        purpose: form.purpose,
        show_publicly: showPublicly,
      });
      const { order_id, amount: amt, currency, key_id } = orderRes.data;

      const options = {
        key: key_id,
        amount: amt,
        currency,
        name: "Dharmoday Seva Sansthan",
        description: form.purpose,
        order_id,
        prefill: {
          name: form.donor_name,
          email: form.donor_email,
          contact: form.donor_phone,
        },
        theme: { color: "#C95B24" },
        handler: async (resp) => {
          try {
            await api.post("/payments/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
              donor_name: form.donor_name,
              donor_email: form.donor_email,
              donor_phone: form.donor_phone,
              purpose: form.purpose,
              amount,
              show_publicly: showPublicly,
            });
            toast.success(lang === "hi" ? "धन्यवाद! आपका दान सफल रहा।" : "Thank you! Donation successful.");
            setForm({ donor_name: "", donor_email: "", donor_phone: "", purpose: "General Donation" });
          } catch (err) {
            toast.error("Verification failed");
          }
        },
        modal: {
          ondismiss: () => setLoading(false),
        },
      };
      const rz = new window.Razorpay(options);
      rz.open();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Payment failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20" data-testid="donate-page">
      <div className="mb-14 max-w-3xl animate-fade-up">
        <p className="text-xs uppercase tracking-[0.25em] text-[#C95B24] font-semibold mb-3">{t("donate")}</p>
        <h1 className="font-serif-display text-5xl sm:text-6xl text-[#1B4332] mb-4 leading-[1.05]">{t("donateTitle")}</h1>
        <p className="text-lg text-[#4A4A4A]">{t("donateSub")}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
        {/* Impact panel */}
        <div className="lg:col-span-2 space-y-4">
          <h3 className="font-serif-display text-2xl text-[#1B4332] mb-2">
            {lang === "hi" ? "आपका प्रभाव" : "Your Impact"}
          </h3>
          {impactData.map((it, i) => (
            <button
              key={i}
              onClick={() => setAmount(it.amount)}
              data-testid={`impact-preset-${it.amount}`}
              className={`w-full text-left card-tactile p-5 flex items-center justify-between ${amount === it.amount ? "border-[#C95B24] ring-2 ring-[#C95B24]/20" : ""}`}
            >
              <div>
                <div className="font-serif-display text-2xl text-[#1B4332]">₹ {it.amount}</div>
                <div className="text-sm text-[#4A4A4A]">{lang === "hi" ? it.hi : it.en}</div>
              </div>
              <IndianRupee className="w-5 h-5 text-[#C95B24]" />
            </button>
          ))}
        </div>

        {/* Form */}
        <form onSubmit={handlePay} className="lg:col-span-3 card-tactile p-8 md:p-10 space-y-5" data-testid="donate-form">
          {!config.configured && (
            <div className="p-4 rounded-md border border-[#C95B24]/30 bg-[#F4EFE6] text-sm text-[#4A4A4A]" data-testid="payment-not-configured">
              {lang === "hi"
                ? "भुगतान गेटवे अभी कॉन्फ़िगर नहीं है। कृपया व्यवस्थापक से संपर्क करें या नीचे विवरण भरें — हम आपसे संपर्क करेंगे।"
                : "Payment gateway not yet configured. Please contact admin or fill your details below — we'll reach out to you."}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="d-name">{t("donateName")}</Label>
              <Input id="d-name" data-testid="donor-name-input" required value={form.donor_name} onChange={(e) => setForm({ ...form, donor_name: e.target.value })} className="mt-2" />
            </div>
            <div>
              <Label htmlFor="d-email">{t("donateEmail")}</Label>
              <Input id="d-email" type="email" data-testid="donor-email-input" required value={form.donor_email} onChange={(e) => setForm({ ...form, donor_email: e.target.value })} className="mt-2" />
            </div>
          </div>
          <div>
            <Label htmlFor="d-phone">{t("donatePhone")}</Label>
            <Input id="d-phone" data-testid="donor-phone-input" value={form.donor_phone} onChange={(e) => setForm({ ...form, donor_phone: e.target.value })} className="mt-2" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="d-amount">{t("donateAmount")}</Label>
              <Input id="d-amount" type="number" min="1" data-testid="donor-amount-input" required value={amount} onChange={(e) => setAmount(Number(e.target.value))} className="mt-2" />
            </div>
            <div>
              <Label>{t("donatePurpose")}</Label>
              <Select value={form.purpose} onValueChange={(v) => setForm({ ...form, purpose: v })}>
                <SelectTrigger data-testid="donor-purpose-select" className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {purposes.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{lang === "hi" ? p.hi : p.en}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Checkbox id="show-pub" checked={showPublicly} onCheckedChange={(v) => setShowPublicly(!!v)} data-testid="donor-show-publicly-checkbox" />
            <Label htmlFor="show-pub" className="cursor-pointer text-sm">{t("donateShowPublicly")}</Label>
          </div>
          <Button type="submit" disabled={loading || !config.configured} data-testid="donate-submit-btn" className="btn-saffron rounded-full w-full py-6 text-base">
            <Heart className="w-5 h-5 mr-2" /> {loading ? "..." : `${t("donateSubmit")} ₹${amount}`}
          </Button>
          <p className="text-xs text-[#4A4A4A] text-center">
            {lang === "hi" ? "आपका दान सुरक्षित रूप से Razorpay के माध्यम से संसाधित होता है।" : "Your donation is securely processed via Razorpay."}
          </p>
        </form>
      </div>
    </div>
  );
};

export default Donate;
